import React, { useState } from 'react';
import './login-screen.scss';

interface LoginScreenProps {
  onComplete: (userData: { username: string; mobileNumber: string }) => void;
}

const LoginScreen: React.FC<LoginScreenProps> = ({ onComplete }) => {
  const [username, setUsername] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{ username?: string; mobileNumber?: string }>({});

  const validateForm = () => {
    const newErrors: { username?: string; mobileNumber?: string } = {};
    
    if (!username.trim()) {
      newErrors.username = 'Username is required';
    } else if (username.trim().length < 2) {
      newErrors.username = 'Username must be at least 2 characters';
    }
    
    if (!mobileNumber.trim()) {
      newErrors.mobileNumber = 'Mobile number is required';
    } else if (!/^\+?[\d\s-()]{8,15}$/.test(mobileNumber.trim())) {
      newErrors.mobileNumber = 'Please enter a valid mobile number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    
    // Simulate API call delay (since it's dummy for now)
    setTimeout(() => {
      const userData = {
        username: username.trim(),
        mobileNumber: mobileNumber.trim()
      };
      
      // Store user data in localStorage for persistence (dummy implementation)
      localStorage.setItem('aqary_user_data', JSON.stringify(userData));
      
      onComplete(userData);
      setIsLoading(false);
    }, 1000);
  };

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value);
    if (errors.username) {
      setErrors(prev => ({ ...prev, username: undefined }));
    }
  };

  const handleMobileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMobileNumber(e.target.value);
    if (errors.mobileNumber) {
      setErrors(prev => ({ ...prev, mobileNumber: undefined }));
    }
  };

  return (
    <div className="login-screen">
      <div className="login-content">
        <div className="login-header">
          <div className="logo-container">
            <img src="/aqary-logo.png" alt="Aqary" className="login-logo" />
          </div>
          <h1>Welcome to Aqary</h1>
          <p className="login-subtitle">
            Please enter your details to continue
          </p>
        </div>

        <form className="login-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="username" className="form-label">
              Username
            </label>
            <input
              type="text"
              id="username"
              className={`form-input ${errors.username ? 'error' : ''}`}
              value={username}
              onChange={handleUsernameChange}
              placeholder="Enter your username"
              disabled={isLoading}
            />
            {errors.username && (
              <span className="error-message">{errors.username}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="mobileNumber" className="form-label">
              Mobile Number
            </label>
            <input
              type="tel"
              id="mobileNumber"
              className={`form-input ${errors.mobileNumber ? 'error' : ''}`}
              value={mobileNumber}
              onChange={handleMobileChange}
              placeholder="Enter your mobile number"
              disabled={isLoading}
            />
            {errors.mobileNumber && (
              <span className="error-message">{errors.mobileNumber}</span>
            )}
          </div>

          <button
            type="submit"
            className="login-button"
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="loading-content">
                <span className="spinner"></span>
                Logging in...
              </span>
            ) : (
              'Continue'
            )}
          </button>
        </form>

        <div className="login-footer">
          <p className="disclaimer">
            This is a demo version. Any username and mobile number will work.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;
